import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import AppStoreButtons from './AppStoreButtons';

const FooterContainer = styled.footer`
  background-color: var(--bg-primary);
  border-top: 1px solid var(--border-color);
  padding: 2rem 0;
  margin-top: auto;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const FooterSection = styled.div`
  h3 {
    color: var(--text-primary);
    font-size: 1.125rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }
  
  p {
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 0.5rem;
  }
  
  a {
    color: var(--text-secondary);
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: var(--primary-color);
    }
  }
`;

const FooterBottom = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
  text-align: center;
  border-top: 1px solid var(--border-color);
  margin-top: 2rem;
  
  p {
    color: var(--text-light);
    font-size: 0.875rem;
  }
`;

const AskFleaButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.5rem 1rem;
  background-color: #25D366;
  color: white !important;
  border-radius: 2rem;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: background-color 0.2s ease, transform 0.1s ease;
  
  &:hover {
    background-color: #1da851;
    color: white !important;
    transform: translateY(-1px);
  }
`;

const WhatsAppIcon = styled.span`
  display: flex;
  align-items: center;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const StoreButtonsWrap = styled.div`
  margin-top: 1rem;
`;

const Footer = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');

  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <h3>Flea</h3>
          <p>{t('footer.brandDescription')}</p>
          <StoreButtonsWrap>
            <h3>{t('footer.getTheApp')}</h3>
            <AppStoreButtons align="start" />
          </StoreButtonsWrap>
        </FooterSection>
        
        <FooterSection>
          <h3>{t('footer.support')}</h3>
          <p><Link to="/support">{t('footer.contactSupport')}</Link></p>
          <p><Link to="/faq">{t('footer.faq')}</Link></p>
          <p><Link to="/terms">{t('footer.termsOfService')}</Link></p>
          <p><Link to="/privacy">{t('footer.privacyPolicy')}</Link></p>
        </FooterSection>

        <FooterSection>
          <h3>{isArabic ? 'اكتشف فلي' : 'Explore Flea'}</h3>
          <p><Link to={isArabic ? '/ar/second-hand-egypt' : '/second-hand-egypt'}>{isArabic ? 'سوق المستعمل والبالة' : 'Second-hand marketplace'}</Link></p>
          <p><Link to={isArabic ? '/ar/thrift-egypt' : '/thrift-egypt'}>{isArabic ? 'ثريفت وقطع مميزة' : 'Thrift and pre-loved finds'}</Link></p>
          <p><Link to={isArabic ? '/ar/flea-market-egypt' : '/flea-market-egypt'}>{isArabic ? 'فلي ماركت أونلاين' : 'Online flea market'}</Link></p>
        </FooterSection>
        
        <FooterSection>
          <h3>{t('footer.connect')}</h3>
          <p>{t('footer.email')}: <a href="mailto:support@flea-marketplace.com">support@flea-marketplace.com</a></p>
          <p>{t('footer.phone')}: (+20)1040895962</p>
          <p>
            {t('footer.followUs')}{' '}
            <a 
              href="https://www.instagram.com/flea.eg/" 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label={t('footer.followUsAria')}
            >
              @flea.eg
            </a>
          </p>
          <AskFleaButton
            href="https://wa.me/201040895962"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('footer.whatsAppAria')}
          >
            <WhatsAppIcon>
              <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.117 1.528 5.845L.057 23.545a.5.5 0 0 0 .612.612l5.7-1.471A11.943 11.943 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.9 0-3.68-.513-5.21-1.408l-.374-.223-3.884 1.002 1.002-3.884-.223-.374A9.943 9.943 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
              </svg>
            </WhatsAppIcon>
            {t('footer.askFlea')}
          </AskFleaButton>
        </FooterSection>
      </FooterContent>
      
      <FooterBottom>
        <p>{t('footer.copyright')}</p>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;
